import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { PropertyCard } from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { supabase } from '../lib/supabase';
import type { DatabaseCategory } from '../lib/supabase';
import type { Property } from '../data/mockData';
import { Search, Home as HomeIcon, Building2, Briefcase, Store, House, CheckCircle, Shield, Users, Loader2, Phone, Mail } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { useCategories } from '../contexts/CategoriesContext';

const defaultHeroImage = 'https://images.unsplash.com/photo-1722578460172-34a3c24687f3?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMGVneXB0JTIwYXJjaGl0ZWN0dXJlfGVufDF8fHx8MTc3MjYxNjQ1M3ww&ixlib=rb-4.1.0&q=80&w=1080';

export default function Home() {
  const { settings } = useSettings();
  const { categories, loading: categoriesLoading } = useCategories();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [searchCategory, setSearchCategory] = useState('all');
  const [searchLocation, setSearchLocation] = useState('all');
  const [featuredProperties, setFeaturedProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const isLoading = loading || categoriesLoading;

  useEffect(() => {
    fetchHomeData();
  }, []);

  const fetchHomeData = async () => {
    try {
      setLoading(true);
      
      // Fetch featured properties
      const { data: propertiesData, error: propertiesError } = await supabase
        .from('properties')
        .select('*')
        .eq('featured', true)
        .eq('active', true)
        .order('created_at', { ascending: false })
        .limit(3);
      
      if (propertiesError) throw propertiesError;
      
      // Transform snake_case to camelCase for properties
      const transformedProperties = (propertiesData || []).map(p => ({
        ...p,
        mainImage: p.main_image || p.mainImage,
        galleryImages: p.gallery_images || p.galleryImages || [],
        videoUrl: p.video_url || p.videoUrl,
        adminNote: p.admin_note || p.adminNote,
        categoryId: p.category_id || p.categoryId,
        createdAt: p.created_at || p.createdAt,
      }));
      
      setFeaturedProperties(transformedProperties);
    } catch (err) {
      console.error('Error fetching home data:', err);
    } finally {
      setLoading(false);
    }
  };

  const heroImage = settings.heroBackgroundImage || defaultHeroImage;

  const getCategoryIcon = (name: string) => {
    switch (name) {
      case 'شقق':
        return Building2;
      case 'فلل':
        return HomeIcon;
      case 'مكاتب':
        return Briefcase;
      case 'محلات تجارية':
        return Store;
      case 'بنغل':
        return House;
      default:
        return Building2;
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchKeyword) params.set('keyword', searchKeyword);
    if (searchType !== 'all') params.set('type', searchType);
    if (searchCategory !== 'all') params.set('category', searchCategory);
    if (searchLocation !== 'all') params.set('location', searchLocation);
    
    window.location.href = `/properties?${params.toString()}`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[600px] md:h-[700px]">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>

        <div className="relative container mx-auto px-4 h-full flex flex-col justify-center items-center text-center">
          {/* Welcome Text */}
          <div className="mb-4 flex items-center gap-2 text-[#FFE5A0]">
            <div className="h-px w-8 bg-[#FFE5A0]"></div>
            <span className="text-sm tracking-wider">{settings.heroWelcomeText}</span>
            <div className="h-px w-8 bg-[#FFE5A0]"></div>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 max-w-4xl">
            <div dangerouslySetInnerHTML={{ __html: settings.heroTitle }} />
          </h1>

          <p className="text-white/90 text-lg md:text-xl mb-8 max-w-2xl">
            {settings.heroSubtitle}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 mb-12">
            <Link to="/properties">
              <Button className="bg-[#D4AF37] hover:bg-[#B8960C] text-white px-8 py-6 text-lg">
                عرض العقارات
              </Button>
            </Link>
            <Link to="#contact">
              <Button variant="outline" className="border-2 border-[var(--color-gold-400)] text-[var(--color-gold-400)] hover:bg-[var(--color-gold-400)] hover:text-white px-8 py-6 text-lg">
                اتصل بنا الآن
              </Button>
            </Link>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-lg shadow-2xl p-6 w-full max-w-5xl">
            <div className="grid md:grid-cols-5 gap-4">
              <div className="md:col-span-1">
                <Input
                  placeholder="كلمات البحث..."
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  className="h-12"
                />
              </div>

              <div>
                <Select value={searchType} onValueChange={setSearchType}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="نوع العملية" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع الأنواع</SelectItem>
                    <SelectItem value="sale">للبيع</SelectItem>
                    <SelectItem value="rent">للإيجار</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Select value={searchCategory} onValueChange={setSearchCategory}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="نوع العقار" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع العقارات</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.nameEn}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Select value={searchLocation} onValueChange={setSearchLocation}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="الموقع" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع المواقع</SelectItem>
                    <SelectItem value="new-cairo">القاهرة الجديدة</SelectItem>
                    <SelectItem value="nasr-city">مدينة نصر</SelectItem>
                    <SelectItem value="maadi">المعادي</SelectItem>
                    <SelectItem value="6th-october">6 أكتوبر</SelectItem>
                    <SelectItem value="alexandria">الإسكندرية</SelectItem>
                    <SelectItem value="giza">الجيزة</SelectItem>
                    <SelectItem value="north-coast">الساحل الشمالي</SelectItem>
                    <SelectItem value="hurghada">الغردقة</SelectItem>
                    <SelectItem value="sharm">شرم الشيخ</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleSearch}
                className="bg-[#D4AF37] hover:bg-[#B8960C] text-white h-12 text-lg"
              >
                <Search className="w-5 h-5 ml-2" />
                بحث
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Property Types Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 text-[#D4AF37] mb-3">
              <div className="h-px w-8 bg-[#D4AF37]"></div>
              <span className="text-sm tracking-wider">أواع العقارات</span>
              <div className="h-px w-8 bg-[#D4AF37]"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#8B7109] mb-4">
              استكشف أنواع الشقق
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {categories.map((category) => {
              const Icon = getCategoryIcon(category.name);
              return (
                <Link
                  key={category.id}
                  to={`/properties?category=${category.nameEn}`}
                  className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-[#D4AF37] hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-[#D4AF37] group-hover:bg-[#B8960C] rounded-full flex items-center justify-center mb-4 transition-colors">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-bold text-gray-800 mb-2">{category.name}</h3>
                    <p className="text-sm text-gray-600">{(category as any).count || 0} عقار</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 text-[#D4AF37] mb-3">
              <div className="h-px w-8 bg-[#D4AF37]"></div>
              <span className="text-sm tracking-wider">العقارات</span>
              <div className="h-px w-8 bg-[#D4AF37]"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#8B7109] mb-4">
              العقارات المميزة
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {isLoading ? (
              <div className="col-span-3 flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
              </div>
            ) : featuredProperties.length > 0 ? (
              featuredProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))
            ) : (
              <div className="col-span-3 text-center py-12 text-gray-500">
                لا توجد عقارات مميزة متاحة
              </div>
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/properties">
              <Button className="bg-[#D4AF37] hover:bg-[#B8960C] text-white px-8 py-6 text-lg">
                عرض جميع العقارات
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 text-[#D4AF37] mb-3">
              <div className="h-px w-8 bg-[#D4AF37]"></div>
              <span className="text-sm tracking-wider">لماذا نحن</span>
              <div className="h-px w-8 bg-[#D4AF37]"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#8B7109] mb-4">
              لماذا تختارنا
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-lg bg-gray-50 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">خدمة احترافية</h3>
              <p className="text-gray-600">
                فريق محترف من الخبراء لمساعدتك في كل خطوة من رحلة البحث عن عقارك المثالي
              </p>
            </div>

            <div className="text-center p-8 rounded-lg bg-gray-50 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">عقارات موثوقة</h3>
              <p className="text-gray-600">
                جميع عقاراتنا تم التحقق منها وفحصها بعناية لضمان جودتها ومصداقيتها
              </p>
            </div>

            <div className="text-center p-8 rounded-lg bg-gray-50 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">معاملات آمنة</h3>
              <p className="text-gray-600">
                نضمن لك معاملات آمنة ومحمية بأعلى معايير الأمان والشفافية
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 text-[#D4AF37] mb-3">
              <div className="h-px w-8 bg-[#D4AF37]"></div>
              <span className="text-sm tracking-wider">تواصل معنا</span>
              <div className="h-px w-8 bg-[#D4AF37]"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#8B7109] mb-4">
              اتصل بنا اليوم
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              نحن هنا لمساعدتك في العثور على عقار أحلامك. تواصل معنا لأي استفسارات أو لتحديد موعد.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h3 className="text-2xl font-bold text-[#8B7109] mb-6">معلومات التواصل</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">الهاتف</h4>
                    <p className="text-gray-600">{settings.phone || '+20 123 456 7890'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">البريد الإلكتروني</h4>
                    <p className="text-gray-600">{settings.email || 'info@wagehrealestate.com'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center">
                    <HomeIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">العنوان</h4>
                    <p className="text-gray-600">{settings.address || 'القاهرة، مصر'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-[#8B7109] mb-6">أرسل لنا رسالة</h3>
              <form className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">الاسم</label>
                  <Input placeholder="ادخل اسمك الكامل" className="h-12" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">البريد الإلكتروني</label>
                  <Input type="email" placeholder="ادخل بريدك الإلكتروني" className="h-12" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">الرسالة</label>
                  <textarea 
                    placeholder="اكتب رسالتك هنا..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37] min-h-[120px] resize-none"
                  ></textarea>
                </div>
                <Button className="bg-[#D4AF37] hover:bg-[#B8960C] text-white px-8 py-3 text-lg w-full">
                  إرسال الرسالة
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}